import { useState, useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import { authService } from '../services/authService'
import walletService from '../services/walletService'
import '../styles/deposit-modal.css'
import { apiErrorMessage } from '../utils/apiErrorMessage'

/**
 * OwnerDepositModal
 *
 * Header ke user dropdown ka "Deposit Balance" — SIRF owner ke liye.
 * Owner apne hi wallet me balance daalta hai via
 * POST /admin/wallet/owner/self-deposit (server requireOwner se gate karta hai).
 *
 * Current balance /admin/owner/profile se aata hai (wahi source jo users2 ka
 * Master Balance panel use karta hai) — modal khulte hi fresh fetch hota hai.
 *
 * Behaviour SettlePointsModal jaisa: inline validation, request ke dauraan busy,
 * modal sirf success pe band (fail pe values bachi rehti hain).
 *
 * @param {boolean}  props.show
 * @param {Function} props.onHide
 * @param {Function} props.onSuccess - optional, deposit ke baad parent ko batao
 */
function OwnerDepositModal({ show, onHide, onSuccess }) {
  const [formData, setFormData] = useState({
    amount: '',
    remark: '',
    transactionPassword: ''
  })
  const [profile, setProfile] = useState(null)
  const [loadingBalance, setLoadingBalance] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const num = (v) => {
    const n = parseFloat(v)
    return Number.isFinite(n) ? n : 0
  }
  const fmt = (n) => (Math.round(num(n) * 100) / 100).toFixed(2)

  const fetchBalance = async () => {
    setLoadingBalance(true)
    try {
      const res = await authService.getOwnerProfileV2()
      if (res?.success) setProfile(res.data?.user || null)
    } catch (err) {
      console.error('OwnerDepositModal: balance fetch failed', err)
    } finally {
      setLoadingBalance(false)
    }
  }

  // Modal parent me mounted rehta hai — har open pe form saaf + balance fresh.
  useEffect(() => {
    if (!show) return
    setFormData({ amount: '', remark: '', transactionPassword: '' })
    setError('')
    setBusy(false)
    fetchBalance()
  }, [show])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (error) setError('')
  }

  const currentBalance = num(profile?.inr_balance)
  const amt = num(formData.amount)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (!String(formData.amount).trim() || !Number.isFinite(parseFloat(formData.amount))) {
      setError('Enter valid amount')
      return
    }
    if (amt <= 0) {
      setError('Amount must be greater than 0')
      return
    }
    if (String(formData.remark).trim().length > 200) {
      setError('Remark max 200 characters allowed')
      return
    }
    if (!formData.transactionPassword) {
      setError('Transaction Password required')
      return
    }

    setBusy(true)
    try {
      const res = await walletService.ownerSelfDeposit({
        amount: amt,
        remark: String(formData.remark).trim(),
        transactionPassword: formData.transactionPassword
      })

      if (res?.success) {
        // Success pe modal band — SettlePointsModal jaisa behaviour.
        setFormData({ amount: '', remark: '', transactionPassword: '' })
        onSuccess?.(res.data)
        onHide()
      } else {
        setError(res?.message || res?.error || 'Something went wrong')
      }
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const handleClose = () => {
    if (busy) return
    setFormData({ amount: '', remark: '', transactionPassword: '' })
    setError('')
    onHide()
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      dialogClassName="modal-md modal-dialog-scrollable"
    >
      <Modal.Header>
        <Modal.Title>Deposit Balance</Modal.Title>
        <button
          type="button"
          className="close text-white"
          onClick={handleClose}
          aria-label="Close"
        >
          ×
        </button>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit} method="post" noValidate>
          {/* Current balance | deposit ke baad kya banega */}
          <div className="form-group row">
            <label className="col-form-label col-4">
              {profile?.username || 'Owner'}
            </label>
            <div className="col-8">
              <div className="row">
                <div className="col-6">
                  <input
                    type="text"
                    readOnly
                    className="form-control txt-right"
                    value={loadingBalance ? '...' : fmt(currentBalance)}
                    placeholder="Current Balance"
                    title="Current Balance"
                  />
                </div>
                <div className="col-6">
                  <input
                    type="text"
                    readOnly
                    className="form-control txt-right"
                    value={amt > 0 ? fmt(currentBalance + amt) : ''}
                    placeholder="After"
                    title="Balance after deposit"
                  />
                </div>
              </div>
              <small className="text-muted">Current | After deposit</small>
            </div>
          </div>

          <div className="form-group row">
            <label className="col-form-label col-4">Amount</label>
            <div className="col-8 form-group-feedback form-group-feedback-right">
              <input
                type="number"
                name="amount"
                className="form-control txt-right"
                value={formData.amount}
                onChange={handleChange}
                min="0"
                step="any"
                aria-required="true"
              />
            </div>
          </div>

          <div className="form-group row">
            <label className="col-form-label col-4">Remark</label>
            <div className="col-8 form-group-feedback form-group-feedback-right">
              <textarea
                name="remark"
                className="form-control"
                value={formData.remark}
                onChange={handleChange}
                maxLength={200}
              />
            </div>
          </div>

          <div className="form-group row">
            <label className="col-form-label col-4">Transaction Password</label>
            <div className="col-8 form-group-feedback form-group-feedback-right">
              <input
                name="transactionPassword"
                type="password"
                className="form-control"
                value={formData.transactionPassword}
                onChange={handleChange}
                autoComplete="off"
                aria-required="true"
              />
            </div>
          </div>

          {error && (
            <div className="form-group row">
              <div className="col-12 text-right text-danger">{error}</div>
            </div>
          )}

          <div className="form-group row">
            <div className="col-12 text-right">
              <button
                type="button"
                className="btn btn-back"
                onClick={handleClose}
                disabled={busy}
              >
                <i className="fas fa-undo mr-1"></i>
                Back
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={busy}
              >
                submit
                <i className="fas fa-sign-in-alt ml-1"></i>
              </button>
            </div>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  )
}

export default OwnerDepositModal
